import { Button, Divider, Flex, Popover, Spin, Tag, Tooltip, Typography } from 'antd'
import { useParams } from "react-router-dom"
import { useGetMoneyFlow } from "../api/case/document.ts"
import {
    useTraceMutation,
} from "../api/tracing/tracing.ts"
import { useMoneyflowVersionsModal } from '../modals/MoneyflowVersionsModal/useMoneyflowVersionsModal.ts'
import { useTracingStatuses } from "../hooks/useTracingStatuses.ts"
import { useCurrentStep } from "../hooks/useCurrentStep.ts"
import { TraceDirection } from "../backend/types/moneyflow.ts"
import { useGetSankeyData } from "../api/sankeyData/get.ts"
import { useGetMoneyflowVersions } from "../api/moneyflow/moneyflow.ts"
import { TaskStatus } from "../backend/types/task.ts"
import { InfoCircleOutlined } from '@ant-design/icons'

const MONEYFLOW_LIGHT_DEPTH_LIMIT = 10

export const MoneyFlowControls = () => {
    const { documentId } = useParams()
    const { mutate: trace, isPending: isMakingTraceRequest } = useTraceMutation(documentId!)
    const { currentStep } = useCurrentStep()
    const { data: moneyFlow, isFetching } = useGetMoneyFlow(documentId)
    const { data: versions, isFetching: versionsFetching } = useGetMoneyflowVersions(documentId!)
    const { data: sankeyData } = useGetSankeyData(moneyFlow?.sankeyData[currentStep])
    const { In, Out } = useTracingStatuses()
    const { show: showMoneyFlowVersions } = useMoneyflowVersionsModal()

    const {
        inboundDepth = 0, outboundDepth = 0
    } = sankeyData ?? {}
    const {
        blocked: moneyflowIsBlocked, forceResolution
    } = moneyFlow || {}

    const reachedInboundLimit = inboundDepth >= MONEYFLOW_LIGHT_DEPTH_LIMIT
    const reachedOutboundLimit = outboundDepth >= MONEYFLOW_LIGHT_DEPTH_LIMIT

    const isResolving =
        forceResolution?.status === TaskStatus.Queued ||
        forceResolution?.status === TaskStatus.Running

    const traceOnce = (direction: TraceDirection) => {
        if (!moneyFlow) return
        trace({
            documentId: documentId!,
            tracingId: moneyFlow.content.tracingId,
            direction,
            name: `${moneyFlow!.name} - ${direction === 'in' ?
                "Inbound " + (inboundDepth + 1) :
                "Outbound " + (outboundDepth + 1)}`
        })
    }

    function handleTrace(direction: TraceDirection) {
        traceOnce(direction)
    }

    const MFVersion = versions ? versions.map((v) => v._id).indexOf(documentId!) + 1 : 0

    const renderPopoverContent = () => (
        <Typography.Text>
            Maximum {MONEYFLOW_LIGHT_DEPTH_LIMIT} hops in each direction
        </Typography.Text>
    )

    return (
        <Flex gap={8} align="center" wrap="wrap" style={{ flex: '1 1 auto' }}>
            <Button
                disabled={isFetching}
                size="small"
                type="primary"
                ghost
                onClick={() => showMoneyFlowVersions()}
            >

                V{MFVersion}.{currentStep}
                <Divider type="vertical"/>
                <Typography.Text>
                    In:{versionsFetching ?
                        <Spin spinning={versionsFetching}/> :
                    versions![MFVersion - 1].steps[currentStep].inboundDepth}
                    {' '} –
                    Out:{versionsFetching ?
                        <Spin spinning={versionsFetching}/> :
                    versions![MFVersion - 1].steps[currentStep].outboundDepth}
                </Typography.Text>

            </Button>

            {sankeyData?.truncated && (
                <Tooltip title={
                    'Some hops had more transfers than one trace keeps, so only the largest ' +
                    'of them (by USD value) are shown. Narrow the dates or pick one currency to see them all.'
                }>
                    <Tag color="warning" style={{ marginInlineEnd: 0 }}>Partial</Tag>
                </Tooltip>
            )}

            <Flex align={"center"} gap={4} style={{ marginLeft: 'auto' }}>
                <Typography.Text strong>Tracing:</Typography.Text>
                <Popover title={reachedInboundLimit ? renderPopoverContent() : null}>
                    <Button
                        style={{ width: 100 }}
                        type="primary"
                        ghost
                        loading={In.isTracingRunning || isMakingTraceRequest}
                        disabled={In.isTracingRunning || reachedInboundLimit || moneyflowIsBlocked || isResolving}
                        size="small"
                        onClick={() => handleTrace('in')}>
                        Trace In
                        {reachedInboundLimit && <InfoCircleOutlined />}
                    </Button>
                </Popover>
                <Divider type="vertical"/>
                <Popover title={reachedOutboundLimit ? renderPopoverContent() : null}>
                    <Button
                        style={{ width: 100 }}
                        type="primary"
                        ghost
                        loading={Out.isTracingRunning || isMakingTraceRequest}
                        disabled={Out.isTracingRunning || reachedOutboundLimit || moneyflowIsBlocked || isResolving}
                        size="small"
                        onClick={() => handleTrace('out')}>
                        Trace Out
                        {reachedOutboundLimit && <InfoCircleOutlined />}
                    </Button>
                </Popover>
            </Flex>
        </Flex>
    )
}
